import axios from 'axios';
import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

// Response types
export interface SpeculoOAuthTokenResponse {
  accessToken: string;
  tokenType: string;
  clientId: string;
  fingerprint?: string;
  scope: string;
  scopes: string[];
  expiresIn: number | null;
  existing?: boolean;
}

export interface OAuthClientInfo {
  clientId: string;
  scope: string;
  scopes: string[];
  createdAt?: string;
  existing: boolean;
}

/**
 * Get or create Speculo OAuth token
 * This creates OAuth credentials for the organization or returns existing ones
 * @param orgId - Optional organization ID to override the default from auth
 */
export interface QueuedOAuthResponse {
  success: boolean;
  queued: true;
  request_id: string;
  organization_id: string;
  endpoint: string;
}

export const getSpeculoOAuthToken = async (
  orgId?: string
): Promise<SpeculoOAuthTokenResponse | QueuedOAuthResponse> => {
  const response = await remiApi.post<SpeculoOAuthTokenResponse | QueuedOAuthResponse>(
    '/api/v1/oauth/client',
    orgId ? { orgId } : {}
  );

  return response.data;
};

/**
 * Poll GET /api/v1/oauth/token until the downstream consumer has created the client.
 * Retries up to maxAttempts times with delayMs between each.
 */
export const pollOAuthToken = async (
  orgId?: string,
  maxAttempts = 10,
  delayMs = 2000
): Promise<SpeculoOAuthTokenResponse | null> => {
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((r) => setTimeout(r, delayMs));
    try {
      const response = await remiApi.get<SpeculoOAuthTokenResponse>(
        '/api/v1/oauth/token',
        { params: orgId ? { organization_id: orgId } : {} }
      );
      if (response.data?.clientId) return response.data;
    } catch {
      // not ready yet — keep polling
    }
  }
  return null;
};

/**
 * Refresh Speculo OAuth token
 * Gets a fresh Speculo OAuth access token using the refresh token stored on backend
 */
export const refreshSpeculoOAuthToken = async (
  orgId?: string
): Promise<SpeculoOAuthTokenResponse> => {
  const response = await remiApi.get<SpeculoOAuthTokenResponse>('/api/v1/oauth/token', {
    params: orgId ? { organization_id: orgId } : {},
  });

  return response.data;
};

/**
 * Get OAuth client information without generating a new token
 * This is useful for checking if OAuth client exists
 */
export const getOAuthClientInfo = async (orgId?: string): Promise<OAuthClientInfo | null> => {
  try {
    const result = await getSpeculoOAuthToken(orgId);
    return {
      clientId: result.clientId,
      scope: result.scope,
      scopes: result.scopes,
      existing: result.existing || false,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};
